import SwitchButton from "@components/buttons/SwitchButton"
import { MenuSwatchProps } from "@components/MenuColorSwatches"
import { ItemContainer } from "@components/MenuItem"
import MenuItemAccordion from "@components/MenuItemAccordion"
import MenuItemColorSwatches, {
  anchorStylesSideNav,
} from "@components/MenuItemColorSwatches"
import { MenuItemTogglableCompactProps } from "@components/MenuItemTogglableCompact"
import animation, { cubicBezierBackgroundColor } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsRegular13Styles } from "@constants/typography"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  display: none;
  overflow: hidden;

  @media ${breakpoints.laptop} {
    display: initial;
  }
`

const SpecialItem = styled(ItemContainer)`
  padding: 7.5px 0 7.5px 24px;
`

const ResetText = styled.div`
  ${smallcapsRegular13Styles}
  color: ${colors.mediumGray};
`

const ResetButton = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 40px;
  transition: border ${animation.quick} ${cubicBezierBackgroundColor};
  background-color: transparent;
  border: solid 1px ${colors.dividerLineGray};

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }

  &:hover {
    border: solid 1px ${colors.black};
    cursor: pointer;

    ${ResetText} {
      color: ${colors.black};
    }
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.dividerLineGray};
`

interface ProductFilterDesktopProps {
  activeBrands: number
  activeColors: number
  activeDiscountTiers: number
  activeProductSeries: number
  activeSubTypes: number
  brands: Array<MenuItemTogglableCompactProps>
  colors: Array<MenuSwatchProps>
  devices: Array<MenuItemTogglableCompactProps>
  discountTiers: Array<MenuItemTogglableCompactProps>
  hasBundleProducts: boolean
  hasMagnetCompatibleProducts: boolean
  hasPrintedDesigns: boolean
  isClearance: boolean
  isCustomPrints: boolean
  onResetFilters: (event) => void
  onToggleBundleProducts: () => void
  onToggleClearance: (event) => void
  onToggleInStock: (event) => void
  onToggleMagnetCompatibleProducts: () => void
  onTogglePrintedDesigns: () => void
  productSeries: Array<MenuItemTogglableCompactProps>
  showBundleProducts: boolean
  showClearance: boolean
  showInStock: boolean
  showMagnetCompatibleProducts: boolean
  showPrintedDesigns: boolean
  subTypes: Array<MenuItemTogglableCompactProps>
}

const ProductFilterDesktop: React.FC<ProductFilterDesktopProps> = ({
  activeBrands,
  activeColors,
  activeDiscountTiers,
  activeProductSeries,
  activeSubTypes,
  brands,
  colors: colorsProp,
  devices,
  discountTiers,
  hasBundleProducts,
  hasMagnetCompatibleProducts,
  hasPrintedDesigns,
  isClearance,
  isCustomPrints,
  onResetFilters,
  onToggleBundleProducts,
  onToggleClearance,
  onToggleInStock,
  onToggleMagnetCompatibleProducts,
  onTogglePrintedDesigns,
  productSeries,
  showBundleProducts,
  showClearance,
  showInStock,
  showMagnetCompatibleProducts,
  showPrintedDesigns,
  subTypes,
}) => {
  let adjustedSubTypes = subTypes
  let adjustedActiveSubTypes = activeSubTypes

  if (hasPrintedDesigns && subTypes.find(({ label }) => label === "Cases")) {
    const [cases, rest] = _.partition(
      subTypes,
      ({ label }) => label === "Cases"
    )
    adjustedSubTypes = [
      cases[0],
      {
        label: "Custom Cases",
        onClick: () => onTogglePrintedDesigns(),
        toggled: showPrintedDesigns,
      },
      ...rest,
    ].filter((f) => !!f)

    adjustedActiveSubTypes = activeSubTypes + (showPrintedDesigns ? 1 : 0)
  }

  return (
    <Container id="ProductFilterDesktop">
      {!isCustomPrints && colorsProp.length > 1 && (
        <>
          <MenuItemColorSwatches
            activeColorCount={activeColors}
            anchorContainerStyles={anchorStylesSideNav}
            anchorId="ProductFilterDesktop"
            colors={colorsProp}
            expandByDefault
          />
          <Divider />
        </>
      )}
      {brands.length > 1 && (
        <>
          <MenuItemAccordion
            activeItemCount={activeBrands}
            expandByDefault
            items={brands}
            label="Brand"
          />
          <Divider />
        </>
      )}
      {productSeries.length > 1 && (
        <>
          <MenuItemAccordion
            activeItemCount={activeProductSeries}
            expandByDefault={!isClearance}
            items={productSeries}
            label="Series"
          />
          <Divider />
        </>
      )}
      {(hasBundleProducts || hasMagnetCompatibleProducts) && (
        <>
          <MenuItemAccordion
            activeItemCount={
              showBundleProducts && showMagnetCompatibleProducts
                ? 2
                : showBundleProducts
                  ? 1
                  : showMagnetCompatibleProducts
                    ? 1
                    : 0
            }
            expandByDefault
            items={
              hasBundleProducts && hasMagnetCompatibleProducts
                ? [
                    {
                      label: "Bundle Product",
                      onClick: onToggleBundleProducts,
                      toggled: showBundleProducts,
                    },
                    {
                      label: "Built-in Magnet",
                      onClick: onToggleMagnetCompatibleProducts,
                      toggled: showMagnetCompatibleProducts,
                    },
                  ]
                : hasBundleProducts
                  ? [
                      {
                        label: "Bundle Product",
                        onClick: onToggleBundleProducts,
                        toggled: showBundleProducts,
                      },
                    ]
                  : [
                      {
                        label: "Built-in Magnet",
                        onClick: onToggleMagnetCompatibleProducts,
                        toggled: showMagnetCompatibleProducts,
                      },
                    ]
            }
            label="Feature"
          />
          <Divider />
        </>
      )}
      {subTypes.length > 1 && (
        <>
          <MenuItemAccordion
            activeItemCount={adjustedActiveSubTypes}
            expandByDefault
            items={adjustedSubTypes}
            label="Type"
          />
          <Divider />
        </>
      )}
      {isCustomPrints && devices.length > 1 && (
        <>
          <MenuItemAccordion expandByDefault items={devices} label="Device" />
          <Divider />
        </>
      )}
      {!isCustomPrints && (
        <SpecialItem
          onClick={(e) => {
            onToggleInStock(e)
            window.scrollTo({ behavior: "smooth", top: 0 })
          }}
        >
          In Stock Only
          <SwitchButton
            active={showInStock}
            ariaLabel={showInStock ? "Show In Stock Only" : "Show All"}
          />
        </SpecialItem>
      )}
      {!isClearance && (
        <SpecialItem
          onClick={(e) => {
            onToggleClearance(e)
            window.scrollTo({ behavior: "smooth", top: 0 })
          }}
        >
          Sale Only
          <SwitchButton
            active={showClearance}
            ariaLabel={showClearance ? "Hide Sale" : "Show Sale"}
          />
        </SpecialItem>
      )}
      {isClearance && (
        <>
          <MenuItemAccordion
            activeItemCount={activeDiscountTiers}
            expandByDefault
            items={discountTiers}
            label="Sale"
          />
        </>
      )}
      <Divider />
      <ResetButton
        onClick={(e) => {
          onResetFilters(e)
          window.scrollTo({ behavior: "smooth", top: 0 })
        }}
      >
        <ResetText>Reset Filters</ResetText>
      </ResetButton>
    </Container>
  )
}

export default ProductFilterDesktop
